/**
 * Main App component with routes and auth guard
 * Following the constitution's auth guard pattern
 */
import { useAuth, useSsoCheck } from '@codespeak11/cognito-passwordless';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import Layout from './components/Layout';
import AuthCallback from './pages/auth/AuthCallback';
import Login from './pages/auth/Login';
import Dashboard from './pages/Dashboard';
import { LibraryPage } from './pages/library';
import { OptimizerPage } from './pages/optimizer';
import { PacksPage } from './pages/packs';
import { PromptsLibraryPage } from './pages/prompts';
import { PromptRunnerPage } from './pages/runner';
import { TemplatesPage } from './pages/templates';
import SettingsPage from './pages/settings/SettingsPage';
import BillingPage from './pages/billing/BillingPage';
import { dynamicBooksApi, packsApi, projectsApi } from './services/api';
import { ProjectsPage } from './pages/projects';

const PENDING_BOOK_ID_KEY = 'promptfab_pending_book_id';

function LoadingSpinner({ message = 'Loading...' }: { message?: string }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        gap: 2,
      }}
    >
      <CircularProgress />
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
    </Box>
  );
}

function AppRoutes() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const handledBookIdRef = useRef<string | null>(null);
  const [isImportingBook, setIsImportingBook] = useState(false);

  // Skip SSO check on auth callback and login pages
  const skipSsoCheck = location.pathname === '/auth/callback' || location.pathname === '/login';
  const { status } = useSsoCheck({ skip: skipSsoCheck });

  useEffect(() => {
    const queryBookId = new URLSearchParams(location.search).get('bookId');
    if (queryBookId) {
      sessionStorage.setItem(PENDING_BOOK_ID_KEY, queryBookId);
    }
  }, [location.search]);

  useEffect(() => {
    if (
      location.pathname === '/auth/callback' ||
      location.pathname === '/login' ||
      isLoading ||
      status === 'checking' ||
      !isAuthenticated
    ) {
      return;
    }

    const params = new URLSearchParams(location.search);
    const bookId = params.get('bookId') || sessionStorage.getItem(PENDING_BOOK_ID_KEY);

    if (!bookId || handledBookIdRef.current === bookId) {
      return;
    }

    // Mark handled immediately to prevent double-trigger
    handledBookIdRef.current = bookId;
    setIsImportingBook(true);

    void (async () => {
      // First check if a project already exists for this bookId (lightweight DynamoDB lookup)
      try {
        const existing = await projectsApi.byBook(bookId);
        const existingProject = existing?.data as
          | { entityId?: string; id?: string; promptPackIds?: unknown[] }
          | undefined;
        const existingProjectId = existingProject?.entityId || existingProject?.id;
        const existingPackCount = Array.isArray(existingProject?.promptPackIds)
          ? existingProject!.promptPackIds!.length
          : 0;
        // Only jump straight to the project when it already has content. An empty
        // project (e.g. from an earlier failed/empty import) falls through to the
        // import below, which repopulates it.
        if (existingProjectId && existingPackCount > 0) {
          sessionStorage.removeItem(PENDING_BOOK_ID_KEY);
          setIsImportingBook(false);
          navigate(`/projects/${existingProjectId}`, { replace: true });
          return;
        }
      } catch (checkError) {
        const status = (checkError as { response?: { status?: number } }).response?.status;
        // 404 = no project exists yet → show import dialog (expected path)
        // Any other error → also show import dialog (best-effort)
        if (status !== 404) {
          console.warn('by-book check returned unexpected error', checkError);
        }
      }

      // No existing project → import it. Prefer letting the backend fetch the
      // prompt-pack server-side, but always fall back to fetching it in the
      // browser (which reliably has the user's token and network access to
      // DynamicBooks) whenever the server-side path fails or returns no project.
      const resolveProjectId = (result: {
        meta?: { projectId?: string };
        data?: { project?: { entityId?: string } };
      } | undefined): string | undefined =>
        result?.meta?.projectId || result?.data?.project?.entityId;

      try {
        let projectId: string | undefined;

        try {
          const result = await packsApi.ensureFromBook(bookId);
          projectId = resolveProjectId(result);
        } catch (serverError) {
          // Non-400 server-side failures (404/500/network) previously fell
          // through silently. Log and retry below with a client-fetched payload.
          console.warn(
            'Server-side prompt-pack import failed; retrying with client-fetched payload',
            serverError
          );
        }

        if (!projectId) {
          const promptPack = await dynamicBooksApi.getPromptPack(bookId);
          const result = await packsApi.ensureFromBook(bookId, promptPack);
          projectId = resolveProjectId(result);
        }

        sessionStorage.removeItem(PENDING_BOOK_ID_KEY);
        setIsImportingBook(false);
        navigate(projectId ? `/projects/${projectId}` : '/projects', { replace: true });
      } catch (error) {
        const maybeAxiosError = error as {
          response?: { status?: number; data?: { error?: { message?: string } } };
          message?: string;
        };

        handledBookIdRef.current = null;
        setIsImportingBook(false);
        console.error('Failed to import prompt pack from bookId query parameter', {
          status: maybeAxiosError.response?.status,
          data: maybeAxiosError.response?.data,
          message: maybeAxiosError.message,
        });
      }
    })();
  }, [isAuthenticated, isLoading, location.pathname, location.search, navigate, status]);

  // Auth callback is always accessible
  if (location.pathname === '/auth/callback') {
    return <AuthCallback />;
  }

  // Login page should always be accessible to break auth redirect loops
  // when the app has stale auth state but missing usable API tokens.
  if (location.pathname === '/login') {
    const forcedLogout = new URLSearchParams(location.search).get('logout') === '1';
    if (isAuthenticated && !forcedLogout) {
      return <Navigate to={`/dashboard${location.search}`} replace />;
    }
    return <Login />;
  }

  // Show loading while checking auth or SSO.
  // Don't block on SSO 'checking' once the user is already authenticated —
  // the check re-runs on re-renders and would cause an infinite flash loop.
  if (isLoading || (!isAuthenticated && status === 'checking')) {
    return <LoadingSpinner message="Checking authentication..." />;
  }

  // Not authenticated - show login
  if (!isAuthenticated) {
    return <Login />;
  }

  // Importing a prompt pack from a ?bookId= link - show progress message
  if (isImportingBook) {
    return <LoadingSpinner message="Importing your prompt pack…" />;
  }

  // Authenticated - show app routes with layout
  return (
    <>
      <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<Navigate to={`/dashboard${location.search}`} replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/optimizer" element={<OptimizerPage />} />
          <Route path="/runner" element={<PromptRunnerPage />} />
          <Route path="/prompts" element={<PromptsLibraryPage />} />
          <Route path="/templates" element={<TemplatesPage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/projects/:id" element={<ProjectsPage />} />
          <Route path="/packs" element={<PacksPage />} />
          <Route path="/library" element={<LibraryPage />} />
          <Route path="/library/:code" element={<LibraryPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>
      </Routes>
    </>
  );
}

export default function App() {
  return <AppRoutes />;
}
